import { BUSINESS_TIMEZONE, PeriodOfTheDay } from '@omecare/utils';
import { intlFormat } from 'date-fns/intlFormat';
import { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '@/assets/logo.svg';
import { AccoutingRetrocessionFeeViewModel } from './use-retrocession-fee.viewmodel';

type RetrocessionFeeDocumentProps = {
  information: NonNullable<AccoutingRetrocessionFeeViewModel['information']>;
  contentRef: Ref<HTMLInputElement>;
  onLogoLoad: () => void;
};

/**
 * Presentational layout of the substitute retrocession note. Rendered by both
 * the single-export page and the ZIP-export worker so the produced PDF is
 * identical in either flow.
 */
export const RetrocessionFeeDocument = ({
  information,
  contentRef,
  onLogoLoad,
}: RetrocessionFeeDocumentProps) => {
  const { t } = useTranslation();

  const agendaSlotTpl = ({
    slot,
  }: {
    slot: {
      id: string;
      startAt: Date;
      periodOfTheDay: PeriodOfTheDay;
    };
  }) => {
    const formattedDay = intlFormat(
      slot.startAt,
      {
        timeZone: BUSINESS_TIMEZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      {
        locale: 'fr',
      },
    );
    const dayTranslationKey = `accounting-retrocession-fee.pdf-template.agenda-slots.day.${slot.periodOfTheDay}`;
    return (
      <li key={slot.id}>
        <span>
          -{' '}
          {t(dayTranslationKey, {
            day: formattedDay,
          })}
        </span>
      </li>
    );
  };

  return (
    <main
      ref={contentRef}
      className="container mx-auto flex min-h-full flex-1 flex-col gap-4 p-12"
    >
      {/* Logo */}
      <div className="flex h-fit items-center justify-center p-4">
        <img
          src={logo}
          onLoad={onLogoLoad}
          alt="omecare"
          className="h-10 w-auto"
        />
      </div>

      {/* Header */}
      <div className="flex flex-col">
        <span>
          {t('accounting-retrocession-fee.pdf-template.header.created-on', {
            date: information.date,
          })}
        </span>
        <span>
          {t(
            information.doctorSubstitute.fullname.key,
            information.doctorSubstitute.fullname.params,
          )}
        </span>

        <span>
          {t(
            'accounting-retrocession-fee.pdf-template.header.doctor-attending-replace-on-period',
            {
              doctorAttending: t(
                information.doctorAttending.fullname.key,
                information.doctorAttending.fullname.params,
              ),
              periodFrom: information.period.from,
              periodTo: information.period.to,
            },
          )}
        </span>
      </div>

      {/* Accounting summary */}
      <div className="flex flex-col">
        <span>
          {t('accounting-retrocession-fee.pdf-template.summary.amount-paid', {
            total: information.amountPaid,
          })}
        </span>
        <span>
          {t('accounting-retrocession-fee.pdf-template.summary.amount-due', {
            total: information.amountDue,
          })}
        </span>
        <span>
          {t(
            'accounting-retrocession-fee.pdf-template.summary.amount-medical-assistant-fee',
            {
              total: information.amountMedicalAssistantFee,
            },
          )}
        </span>
        <span>
          {t(
            'accounting-retrocession-fee.pdf-template.summary.amount-pay-back',
            {
              retrocessionFee: information.amountPaidRetrocessionFee,
              medicalAssistantFee: information.amountMedicalAssistantFee,
              total: information.amountPayBack,
            },
          )}
        </span>
      </div>

      {/* Accounting agenda slots */}
      <div className="flex flex-col">
        <div>
          {t('accounting-retrocession-fee.pdf-template.agenda-slots.title')}
        </div>

        <ul className="list-inside p-4">
          {information.agendaSlots.map((slot) => {
            return agendaSlotTpl({ slot });
          })}
        </ul>
      </div>

      {/* Signature */}
      <div className="flex flex-col text-left">
        <span>
          {t('accounting-retrocession-fee.pdf-template.signature.best-regards')}
          ,
        </span>
        <span>
          {t(
            'accounting-retrocession-fee.pdf-template.signature.secretary-job-title',
          )}
        </span>
      </div>

      {/* Footer */}
      <div className="flex flex-col text-center">
        <span>OMECARE</span>
        <span>26 rue du Marechal Foch 67500 Haguenau.</span>
        <span>Tel : 03 88 05 22 83 </span>
        <span>SIRET : 978 062 834 00012</span>
        <a
          className="font-medium text-blue-600 underline hover:no-underline dark:text-blue-500"
          href="www.omecare.fr"
        >
          www.omecare.fr
        </a>
      </div>
    </main>
  );
};
